import React, { useState, useEffect, useRef } from "react";
import { useSession } from "hooks/useSession";
import { useRefresh } from "contexts/RefreshContext";
import { fetchWrapper } from "utils/apiFetch";
import { QRCodeSVG } from "qrcode.react";

const UPI_BLUE = "#002970";
const QUICK_AMOUNTS = [100, 500, 1000, 2000];
const UPI_CHIPS = [
  { label: "GPay", color: "#4CAF50" },
  { label: "PhonePe", color: "#673AB7" },
  { label: "Paytm", color: "#00BAF2" },
  { label: "BHIM", color: UPI_BLUE },
];

function formatINR(n) {
  const num = Number(n);
  if (isNaN(num)) return "0";
  return num.toLocaleString("en-IN");
}

function generateTxnId() {
  const timestamp = Date.now();
  const randomSuffix = Math.floor(100000 + Math.random() * 900000);
  return `TXN_${timestamp}_${randomSuffix}`;
}

function getUserProfile() {
  try {
    const raw = localStorage.getItem("userData");
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // ignore
  }
  return {};
}

// ── Toast Notification ────────────────────────────────────────────────────────
function Toast({ message, color, visible }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 32,
        left: "50%",
        transform: `translateX(-50%) translateY(${visible ? 0 : 20}px)`,
        opacity: visible ? 1 : 0,
        transition: "all 0.3s ease",
        background: color || "#333",
        color: "#fff",
        padding: "10px 20px",
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 500,
        zIndex: 9999,
        pointerEvents: "none",
        boxShadow: "0 4px 16px rgba(0,0,0,.2)",
        whiteSpace: "nowrap",
      }}
    >
      {message}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function LoadWalletPayThroughUpi({ show, onClose, title = "Add Money via UPI" }) {
  const { userid, name: sessionName } = useSession();
  const refreshContext = useRefresh();
  const refresh = refreshContext?.refresh;

  // Stages: "FORM" | "PAYMENT" | "SUCCESS" | "FAILED"
  const [stage, setStage] = useState("FORM");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "", color: "#333" });

  const [paymentInfo, setPaymentInfo] = useState(null); // { txnid, intent, paymentUrl, amount }
  const [successData, setSuccessData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [copied, setCopied] = useState(false);

  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  // Reset all state when modal closes
  useEffect(() => {
    if (!show) {
      stopPolling();
      setStage("FORM");
      setAmount("");
      setError("");
      setLoading(false);
      setPaymentInfo(null);
      setSuccessData(null);
      setErrorMessage("");
      setCopied(false);
    }
    return () => {
      stopPolling();
    };
  }, [show]);

  function showToast(message, color = "#333") {
    setToast({ visible: true, message, color });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3500);
  }

  function validate() {
    if (!amount) {
      setError("Please enter an amount");
      return false;
    }
    const n = parseInt(amount, 10);
    if (isNaN(n) || n < 10) {
      setError("Minimum amount is ₹10");
      return false;
    }
    if (n > 50000) {
      setError("Maximum amount is ₹50,000");
      return false;
    }
    setError("");
    return true;
  }

  // ── Initiate Request ────────────────────────────────────────────────────────
  async function initiatePayment() {
    if (!validate()) return;
    setLoading(true);
    setError("");

    const userData = getUserProfile();
    const userName = userData.name || sessionName || localStorage.getItem("name") || "User";
    const userMobile = userData.phone || userData.mobile || localStorage.getItem("phone") || "9876543210";
    const userEmail = userData.email || localStorage.getItem("email") || "user@mokshiq.in";
    const txnid = generateTxnId();

    try {
      const payload = {
        name: userName,
        amount: String(amount),
        email: userEmail,
        mobile: userMobile,
        txnid: txnid,
        gateway: "payzen",
        userid: userid,
      };

      let res = null;
      try {
        res = await fetchWrapper("https://pro.mokshiq.in/api/v2/upicollect", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } catch (err) {
        console.warn("Direct /api/v2/upicollect error:", err);
      }

      // Fallback to /create_order_wallet_topup if upicollect didn't return an intent/payment_url
      const hasIntent = res?.intent || res?.data?.intent || res?.data?.payment_url;
      if (!res || !hasIntent) {
        const fd = new FormData();
        fd.append("name", userName);
        fd.append("amount", amount);
        fd.append("email", userEmail);
        fd.append("mobile", userMobile);
        fd.append("txnid", txnid);
        fd.append("gateway", "payzen");
        fd.append("userid", userid);
        fd.append("remark", "load wallet");

        const fallback = await fetchWrapper("https://pro.mokshiq.in/create_order_wallet_topup", {
          method: "POST",
          body: fd,
        });

        if (fallback?.status === true || fallback?.status === "initiate" || fallback?.intent || fallback?.data?.payment_url) {
          res = fallback;
        }
      }

      const activeTxnid = res?.txnid || res?.data?.txnid || txnid;
      const intent = res?.intent || res?.data?.intent || res?.data?.upi_intent || "";
      const paymentUrl = res?.data?.payment_url || res?.payment_url || "";

      if (!intent && !paymentUrl) {
        const errMsg = res?.message || "Failed to initiate UPI transaction. Please try again.";
        showToast(errMsg, "#d32f2f");
        setError(errMsg);
        setLoading(false);
        return;
      }

      const finalPaymentInfo = {
        txnid: activeTxnid,
        intent: intent,
        paymentUrl: paymentUrl,
        amount: amount,
      };

      setPaymentInfo(finalPaymentInfo);
      setStage("PAYMENT");

      // Handle User Flow: Mobile vs Desktop
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        || (window.innerWidth <= 768 && "ontouchstart" in window);

      if (isMobile && intent) {
        // Open UPI intent directly to launch installed UPI apps (GPay, PhonePe, Paytm)
        window.location.href = intent;
      }

      // Start status polling
      startPolling(activeTxnid);
    } catch (err) {
      console.error("Initiate payment error:", err);
      showToast("Something went wrong while initiating payment", "#d32f2f");
    } finally {
      setLoading(false);
    }
  }

  // ── Status Polling ──────────────────────────────────────────────────────────
  function startPolling(activeTxnid) {
    stopPolling();

    const checkStatus = async () => {
      try {
        const pollRes = await fetchWrapper("https://pro.mokshiq.in/api/v2/checkpayinstatus", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ txnid: activeTxnid }),
        });

        const statusStr = String(pollRes?.status || "").toLowerCase();
        if (statusStr === "success" || statusStr === "true") {
          stopPolling();
          setStage("SUCCESS");
          setSuccessData({
            txnid: activeTxnid,
            amount: pollRes?.amount || amount,
            message: pollRes?.message || "Payment received and wallet credited!",
          });

          // Refresh wallet balance across the app
          if (refresh) refresh();
          window.dispatchEvent(new Event("wallet_updated"));
          window.dispatchEvent(new Event("focus"));
        } else if (statusStr === "failed" || statusStr === "failure") {
          stopPolling();
          setStage("FAILED");
          setErrorMessage(pollRes?.message || "Payment failed or was declined.");
        }
      } catch (err) {
        console.warn("Status check poll error:", err);
      }
    };

    // Poll every 3 seconds
    pollIntervalRef.current = setInterval(checkStatus, 3000);

    // Auto-timeout after 5 minutes of polling
    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      setStage("FAILED");
      setErrorMessage("Payment session timed out. If money was debited, it will reflect in your wallet shortly.");
    }, 300000);
  }

  const handleCopyUPI = () => {
    if (!paymentInfo?.intent) return;
    navigator.clipboard?.writeText(paymentInfo.intent);
    setCopied(true);
    showToast("UPI link copied to clipboard", "#10b981");
    setTimeout(() => setCopied(false), 2500);
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => {
          if (!loading && stage !== "PAYMENT") onClose();
        }}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,.55)",
          zIndex: 1040,
          backdropFilter: "blur(3px)",
        }}
      />

      {/* Modal panel */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          width: "min(480px, 94vw)",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          background: "#F8FAFC",
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0, 41, 112, 0.28)",
          zIndex: 1050,
          animation: "slideUp .28s cubic-bezier(.22,1,.36,1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: UPI_BLUE,
            color: "#fff",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: 0.2 }}>
              {stage === "PAYMENT"
                ? "Scan & Pay via UPI"
                : stage === "SUCCESS"
                ? "Payment Successful"
                : stage === "FAILED"
                ? "Payment Status"
                : title}
            </span>
          </div>

          <button
            onClick={() => {
              stopPolling();
              onClose();
            }}
            style={iconBtnStyle}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          {stage === "FORM" && (
            <FormView
              amount={amount}
              setAmount={(v) => {
                setAmount(v);
                setError("");
              }}
              error={error}
              loading={loading}
              onSubmit={initiatePayment}
            />
          )}

          {stage === "PAYMENT" && paymentInfo && (
            <PaymentQRView
              paymentInfo={paymentInfo}
              copied={copied}
              onCopy={handleCopyUPI}
              onCancel={() => {
                stopPolling();
                setStage("FORM");
              }}
            />
          )}

          {stage === "SUCCESS" && (
            <SuccessView
              data={successData}
              amount={amount}
              onDone={() => {
                stopPolling();
                onClose();
              }}
            />
          )}

          {stage === "FAILED" && (
            <FailedView
              message={errorMessage}
              onRetry={() => {
                setStage("FORM");
                setErrorMessage("");
              }}
              onClose={() => {
                stopPolling();
                onClose();
              }}
            />
          )}
        </div>
      </div>

      <Toast {...toast} />

      <style>{`
        @keyframes slideUp {
          from { opacity:0; transform:translate(-50%, calc(-50% + 24px)); }
          to   { opacity:1; transform:translate(-50%, -50%); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.96); }
        }
        @keyframes bounceIn {
          0% { transform: scale(0.3); opacity: 0; }
          60% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); }
        }
      `}</style>
    </>
  );
}

// ── 1. Form View ──────────────────────────────────────────────────────────────
function FormView({ amount, setAmount, error, loading, onSubmit }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* UPI Chips Header */}
      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
        {UPI_CHIPS.map(({ label, color }) => (
          <div key={label} style={chipStyle}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Amount Input Card */}
      <div style={cardStyle}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", letterSpacing: 0.8 }}>
          ENTER TOP-UP AMOUNT
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
          <span style={{ fontSize: 32, fontWeight: 700, color: UPI_BLUE }}>₹</span>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
            placeholder="0"
            min={10}
            max={50000}
            style={{
              flex: 1,
              fontSize: 32,
              fontWeight: 600,
              border: "none",
              outline: "none",
              background: "transparent",
              color: "#0F172A",
              width: "100%",
            }}
          />
        </div>
        <div
          style={{
            height: 2,
            background: error ? "#EF4444" : UPI_BLUE,
            borderRadius: 2,
            marginTop: 6,
          }}
        />
        {error && <div style={{ color: "#EF4444", fontSize: 12, marginTop: 4 }}>{error}</div>}

        {/* Quick Amount Buttons */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
          {QUICK_AMOUNTS.map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => setAmount(String(amt))}
              style={quickAmtStyle(String(amt) === amount)}
            >
              + ₹{formatINR(amt)}
            </button>
          ))}
        </div>
      </div>

      {/* Info Notice */}
      <div style={infoBannerStyle}>
        <svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }}>
          <circle cx="12" cy="12" r="10" fill={UPI_BLUE} />
          <line x1="12" y1="8" x2="12" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" />
          <line x1="12" y1="16" x2="12.01" y2="16" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span style={{ fontSize: 12, color: "#0C447C", lineHeight: 1.5 }}>
          Money will be added to your Mokshiq wallet immediately upon completion. Min ₹10 · Max ₹50,000.
        </span>
      </div>

      {/* Submit Button */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0" }}>
          <div
            style={{
              width: 32,
              height: 32,
              border: `3px solid ${UPI_BLUE}`,
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin .8s linear infinite",
            }}
          />
        </div>
      ) : (
        <button type="button" onClick={onSubmit} style={payBtnStyle}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}>
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
          Proceed to Pay ₹{amount ? formatINR(parseInt(amount, 10)) : "0"}
        </button>
      )}

      {/* Security badge */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, paddingTop: 2 }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <span style={{ fontSize: 11, color: "#94A3B8" }}>100% Secure · 256-bit Encrypted · Instant Wallet Credit</span>
      </div>
    </div>
  );
}

// ── 2. Payment QR & Polling View ─────────────────────────────────────────────
function PaymentQRView({ paymentInfo, copied, onCopy, onCancel }) {
  const { intent, paymentUrl, amount, txnid } = paymentInfo;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
      {/* Amount Header */}
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B" }}>Total Payable Amount</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: UPI_BLUE, marginTop: 2 }}>
          ₹{formatINR(amount)}
        </div>
      </div>

      {/* QR Code Container */}
      {intent ? (
        <div
          style={{
            background: "#fff",
            padding: 16,
            borderRadius: 16,
            border: "2px solid #E2E8F0",
            boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <QRCodeSVG value={intent} size={190} level="M" includeMargin={true} />
          <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#64748B" }}>Scan with</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#2563EB" }}>GPay</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#7C3AED" }}>PhonePe</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#0284C7" }}>Paytm</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: UPI_BLUE }}>BHIM</span>
          </div>
        </div>
      ) : (
        <div style={{ padding: "20px 0", textAlign: "center" }}>
          <div style={{ fontSize: 14, color: "#475569" }}>Please click below to continue payment</div>
        </div>
      )}

      {/* Live Polling Status Pill */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          background: "#ECFDF5",
          border: "1px solid #A7F3D0",
          borderRadius: 24,
          padding: "6px 14px",
          marginTop: 2,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#10B981",
            animation: "pulse 1.4s infinite ease-in-out",
          }}
        />
        <span style={{ fontSize: 12, fontWeight: 600, color: "#065F46" }}>
          Waiting for payment confirmation...
        </span>
      </div>

      <div style={{ fontSize: 11, color: "#64748B", textAlign: "center", maxWidth: 360 }}>
        Auto-checking status every 3 seconds. Keep this window open until payment completes.
      </div>

      {/* Action Buttons */}
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
        {intent && (
          <button
            type="button"
            onClick={() => {
              window.location.href = intent;
            }}
            style={{
              ...payBtnStyle,
              background: "#00897b",
              padding: "12px 0",
              fontSize: 14,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}>
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
              <line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
            Open in UPI App (Mobile / App)
          </button>
        )}

        {paymentUrl && (
          <button
            type="button"
            onClick={() => window.open(paymentUrl, "_blank")}
            style={{
              ...payBtnStyle,
              background: UPI_BLUE,
              padding: "12px 0",
              fontSize: 14,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}>
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            Open Hosted Checkout Page
          </button>
        )}

        {intent && (
          <button
            type="button"
            onClick={onCopy}
            style={{
              width: "100%",
              background: "#F1F5F9",
              border: "1px solid #CBD5E1",
              borderRadius: 12,
              padding: "10px 0",
              fontSize: 13,
              fontWeight: 600,
              color: "#334155",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            {copied ? "Copied!" : "Copy UPI Payment Link"}
          </button>
        )}

        <button
          type="button"
          onClick={onCancel}
          style={{
            background: "transparent",
            border: "none",
            color: "#64748B",
            fontSize: 13,
            cursor: "pointer",
            marginTop: 4,
            textDecoration: "underline",
          }}
        >
          Cancel & Return
        </button>
      </div>

      <div style={{ fontSize: 10, color: "#94A3B8" }}>
        Ref Txn ID: {txnid}
      </div>
    </div>
  );
}

// ── 3. Success View ───────────────────────────────────────────────────────────
function SuccessView({ data, amount, onDone }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "10px 0 6px" }}>
      {/* Animated Success Badge */}
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "#DCFCE7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: "bounceIn .5s ease",
          marginBottom: 16,
        }}
      >
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>

      <h4 style={{ margin: 0, fontWeight: 700, color: "#0F172A", fontSize: 20 }}>
        Payment Successful!
      </h4>

      <p style={{ margin: "6px 0 16px", color: "#64748B", fontSize: 13 }}>
        ₹{formatINR(data?.amount || amount)} has been added to your Mokshiq wallet.
      </p>

      {/* Transaction Details Box */}
      <div
        style={{
          width: "100%",
          background: "#fff",
          borderRadius: 14,
          padding: "14px 18px",
          border: "1px solid #E2E8F0",
          marginBottom: 20,
          textAlign: "left",
        }}
      >
        <div style={receiptRowStyle}>
          <span style={{ color: "#64748B", fontSize: 12 }}>Status</span>
          <span style={{ color: "#16A34A", fontWeight: 700, fontSize: 12 }}>SUCCESS</span>
        </div>
        <div style={receiptRowStyle}>
          <span style={{ color: "#64748B", fontSize: 12 }}>Transaction ID</span>
          <span style={{ color: "#0F172A", fontWeight: 600, fontSize: 12, wordBreak: "break-all" }}>
            {data?.txnid || "N/A"}
          </span>
        </div>
        <div style={receiptRowStyle}>
          <span style={{ color: "#64748B", fontSize: 12 }}>Amount Credited</span>
          <span style={{ color: "#0F172A", fontWeight: 700, fontSize: 13 }}>
            ₹{formatINR(data?.amount || amount)}
          </span>
        </div>
        <div style={{ ...receiptRowStyle, borderBottom: "none" }}>
          <span style={{ color: "#64748B", fontSize: 12 }}>Payment Method</span>
          <span style={{ color: "#0F172A", fontWeight: 600, fontSize: 12 }}>UPI Instant</span>
        </div>
      </div>

      <button
        type="button"
        onClick={onDone}
        style={{
          ...payBtnStyle,
          background: "#16A34A",
          padding: "12px 0",
        }}
      >
        Done & View Wallet
      </button>
    </div>
  );
}

// ── 4. Failed View ────────────────────────────────────────────────────────────
function FailedView({ message, onRetry, onClose }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "10px 0" }}>
      <div
        style={{
          width: 70,
          height: 70,
          borderRadius: "50%",
          background: "#FEE2E2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 16,
        }}
      >
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>

      <h4 style={{ margin: 0, fontWeight: 700, color: "#0F172A", fontSize: 18 }}>
        Payment Incomplete
      </h4>

      <p style={{ margin: "8px 0 20px", color: "#64748B", fontSize: 13, maxWidth: 360 }}>
        {message || "We could not verify your payment. If money was debited, it will be credited or refunded automatically."}
      </p>

      <div style={{ width: "100%", display: "flex", gap: 10 }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            flex: 1,
            background: "#F1F5F9",
            border: "1px solid #CBD5E1",
            borderRadius: 12,
            padding: "12px 0",
            fontSize: 14,
            fontWeight: 600,
            color: "#475569",
            cursor: "pointer",
          }}
        >
          Close
        </button>
        <button
          type="button"
          onClick={onRetry}
          style={{
            flex: 1,
            ...payBtnStyle,
            padding: "12px 0",
            fontSize: 14,
          }}
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const iconBtnStyle = {
  background: "none",
  border: "none",
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  padding: 4,
  borderRadius: 6,
};

const chipStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 20,
  flexShrink: 0,
};

const cardStyle = {
  background: "#fff",
  borderRadius: 16,
  padding: 20,
  border: "1px solid #E2E8F0",
  boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
};

const infoBannerStyle = {
  background: "#E0F2FE",
  borderRadius: 12,
  padding: "12px 14px",
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  border: "1px solid #BAE6FD",
};

const payBtnStyle = {
  width: "100%",
  background: UPI_BLUE,
  color: "#fff",
  border: "none",
  borderRadius: 14,
  padding: "14px 0",
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  letterSpacing: 0.2,
  transition: "all .15s ease",
  boxShadow: "0 4px 12px rgba(0, 41, 112, 0.25)",
};

function quickAmtStyle(active) {
  return {
    background: active ? "#DBEAFE" : "#F1F5F9",
    border: `1px solid ${active ? UPI_BLUE : "#CBD5E1"}`,
    borderRadius: 20,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    color: active ? UPI_BLUE : "#334155",
    cursor: "pointer",
    transition: "all .15s",
  };
}

const receiptRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 0",
  borderBottom: "1px solid #F1F5F9",
};